/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
// ============================================================================
// USE ERROR STATE - Simple hook for managing error state
// ============================================================================

"use client";

import { useCallback, useState } from "react";

export interface UseErrorStateReturn {
  /**
   * Current error message, or null if no error
   */
  error: string | null;
  /**
   * Set error message
   */
  setError: (error: string | Error | null) => void;
  /**
   * Clear error (set to null)
   */
  clearError: () => void;
  /**
   * Whether there is an error
   */
  hasError: boolean;
}

/**
 * Simple hook for managing error state in components
 *
 * Provides a consistent interface for error state management with
 * automatic conversion of Error objects to strings.
 *
 * @example
 * ```tsx
 * const { error, setError, clearError, hasError } = useErrorState();
 *
 * const handleAction = async () => {
 *   try {
 *     await someAsyncOperation();
 *     clearError();
 *   } catch (err) {
 *     setError(err); // Automatically converts Error to string
 *   }
 * };
 *
 * {hasError && <ErrorDisplay message={error} />}
 * ```
 */
export function useErrorState(
  initialError: string | Error | null = null,
): UseErrorStateReturn {
  const [error, setErrorState] = useState<string | null>(
    initialError instanceof Error
      ? initialError.message
      : (initialError ?? null),
  );

  const setError = useCallback((newError: string | Error | null) => {
    setErrorState(
      newError instanceof Error ? newError.message : (newError ?? null),
    );
  }, []);

  const clearError = useCallback(() => {
    setErrorState(null);
  }, []);

  const hasError = error !== null;

  return {
    error,
    setError,
    clearError,
    hasError,
  };
}
